import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import './App.css';

import { useAuth0 } from '@auth0/auth0-react';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// Import our custom theme
import modernTheme from './theme/modernTheme';

// Layouts
import DashboardLayout from './layouts/DashboardLayout';

// Public Pages
import Home from './pages/public/Home';
import Advertising from './pages/public/Advertising';
import ArtworkSpecs from './pages/public/ArtworkSpecs';
import TermsAndConditions from './pages/public/TermsAndConditions';
import TopSpot from './pages/public/TopSpot';
import Animation from './pages/public/Animation';
import POW from './pages/public/POW';
import PrintDesign from './pages/public/PrintDesign';
import TermsConditionsPrint from './pages/public/TermsConditionsPrint';
import EPublisherKB from './pages/public/EPublisherKB';

// Pages
import Dashboard from './pages/Dashboard';
import Orders from './pages/Orders';
import Login from './pages/Login';
import Users from './pages/Users';
import OrganizationsMockup from './pages/OrganizationsMockup';
import ModernOrganizations from './pages/ModernOrganizations';
import OrganizationOrders from './pages/OrganizationOrders';
import OrderWorkflow from './pages/OrderWorkflow';
import StationeryOrders from './pages/StationeryOrders';
import SalesWorkflow from './pages/SalesWorkflow';
import BusinessOutreach from './pages/BusinessOutreach';
import OutreachContactForm from './pages/OutreachContactForm';
import SchoolWorkflow from './pages/SchoolWorkflow';
import Enquiries from './pages/Enquiries';
import Settings from './pages/Settings';
import Notifications from './pages/Notifications';
import Profile from './pages/Profile/Profile';
import Finance from './pages/Finance';
import Stats from './pages/Stats';


import ProofCreate from './pages/ProofCreate';
import UpdateProofCreate from './pages/UpdateProofCreate';
import ProofView from './pages/ProofView';
import QuoteCreate from './pages/QuoteCreate';
import QuoteAcceptanceDetails from './pages/QuoteAcceptanceDetails';
import OfferCreate from './pages/OfferCreate';
import AttachArtwork from './pages/AttachArtwork';
import Archive from './pages/Archive';


// Auth components
import RoleBasedRoute from './components/Auth/RoleBasedRoute';

// Auth context
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import { GoogleMapsProvider } from './providers/GoogleMapsProvider';
import RouteTracker from './components/RouteTracker';

// Custom hooks
import usePageTitle from './hooks/usePageTitle';
import useScrollToTop from './hooks/useScrollToTop';

// Loading component
const LoadingScreen = () => (
  <Box 
    display="flex" 
    justifyContent="center" 
    alignItems="center" 
    minHeight="100vh"
    flexDirection="column"
    gap={2}
  >
    <CircularProgress size={60} />
  </Box>
);

const PrivateRoute = ({ children }) => {
  const { isAuthenticated, loading, shouldForceLogin, error } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  // If we should force login or have authentication errors, redirect to login
  if (shouldForceLogin || (error && error.includes('Session expired'))) {
    // Store current location for return after login
    localStorage.setItem('returnTo', location.pathname + location.search);
    return <Navigate to="/login" replace />;
  }
  
  if (!isAuthenticated) {
    // Store current location for return after login
    localStorage.setItem('returnTo', location.pathname + location.search);
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return children;
};

// Component to handle dynamic page titles
const AppContent = () => {
  // Use the custom hook to set page title based on route
  usePageTitle();
  // Start public pages at the top when navigating between them
  useScrollToTop();
  
  return (
    <Routes>
            {/* Public Routes */}
            <Route path="/home" element={<Home />} />
            <Route path="/advertising" element={<Advertising />} />
            <Route path="/advertising/artwork-specs" element={<ArtworkSpecs />} />
            <Route path="/advertising/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/advertising/top-spot" element={<TopSpot />} />
            <Route path="/advertising/animation" element={<Animation />} />
            <Route path="/advertising/pow" element={<POW />} />
            <Route path="/design-print" element={<PrintDesign />} />
            <Route path="/design-print/terms-and-conditions" element={<TermsConditionsPrint />} />
            <Route path="/epublisher" element={<Home />} /> {/* Redirect to home for now */}
            {/* ePublisher Knowledge Base - public, direct URL only (intentionally not linked from any page) */}
            <Route path="/epublisher-kb" element={<EPublisherKB />} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/mockup" element={<OrganizationsMockup />} />
            
            {/* Redirect /dashboard to /app */}
            <Route path="/dashboard" element={<Navigate to="/app" replace />} />
            
            {/* Private/Authenticated Routes */}
            <Route 
              path="/app" 
              element={
                <PrivateRoute>
                  <DashboardLayout />
                </PrivateRoute>
              }
            >
              <Route index element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <Dashboard />
                </RoleBasedRoute>
              } />
              <Route path="organizations" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <ModernOrganizations />
                </RoleBasedRoute>
              } />
              <Route path="organizations/:organizationId/orders" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <OrganizationOrders />
                </RoleBasedRoute>
              } />
              <Route path="orders" element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <Orders />
                </RoleBasedRoute>
              } />
              <Route path="workflow" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'designer']}>
                  <OrderWorkflow />
                </RoleBasedRoute>
              } />
              <Route path="order-workflow" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'designer']}>
                  <OrderWorkflow />
                </RoleBasedRoute>
              } />
              <Route path="stationery-orders" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'designer']}>
                  <StationeryOrders />
                </RoleBasedRoute>
              } />
              <Route path="sales-workflow" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales']}>
                  <SalesWorkflow />
                </RoleBasedRoute>
              } />
              <Route path="outreach" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales']}>
                  <BusinessOutreach />
                </RoleBasedRoute>
              } />
              <Route path="outreach/add" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales']}>
                  <OutreachContactForm />
                </RoleBasedRoute>
              } />
              <Route path="outreach/edit/:id" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales']}>
                  <OutreachContactForm />
                </RoleBasedRoute>
              } />
              <Route path="attach-artwork/:orderId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <AttachArtwork />
                </RoleBasedRoute>
              } />
              <Route path="archive" element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <Archive />
                </RoleBasedRoute>
              } />
              <Route path="school-workflow" element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <SchoolWorkflow />
                </RoleBasedRoute>
              } />
              <Route path="enquiries" element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <Enquiries />
                </RoleBasedRoute>
              } />
              <Route path="proof/create/:orderId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'designer']}>
                  <ProofCreate />
                </RoleBasedRoute>
              } />
              <Route path="proof/update/:orderId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'designer']}>
                  <UpdateProofCreate />
                </RoleBasedRoute>
              } />
              <Route path="quote/create/:orderId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <QuoteCreate />
                </RoleBasedRoute>
              } />
              <Route path="quote-acceptance-details/:orderId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <QuoteAcceptanceDetails />
                </RoleBasedRoute>
              } />
              <Route path="proof/view/:orderId/:proofId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <ProofView />
                </RoleBasedRoute>
              } />
              <Route path="offer/create/:offerId" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales']}>
                  <OfferCreate />
                </RoleBasedRoute>
              } />
              <Route path="users" element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <Users />
                </RoleBasedRoute>
              } />

              <Route path="settings" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager']}>
                  <Settings />
                </RoleBasedRoute>
              } />
              
              <Route path="finance" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager']}>
                  <Finance />
                </RoleBasedRoute>
              } />

              <Route path="stats" element={
                <RoleBasedRoute allowedRoles={['admin']}>
                  <Stats />
                </RoleBasedRoute>
              } />
              
              <Route path="profile" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <Profile />
                </RoleBasedRoute>
              } />
              
              <Route path="notifications" element={
                <RoleBasedRoute allowedRoles={['admin', 'manager', 'sales', 'designer']}>
                  <Notifications />
                </RoleBasedRoute>
              } />

            </Route>
            
            {/* Redirect root to home page */}
            <Route path="/" element={<Navigate to="/home" replace />} />
    </Routes>
  );
};

function App() {
  const { isLoading } = useAuth0();

  // Show loading screen while Auth0 is initializing
  if (isLoading) {
    return <LoadingScreen />;
  }

  return (
    <ThemeProvider theme={modernTheme}>
      <CssBaseline />
      <AuthProvider>
        <SocketProvider>
          <NotificationProvider>
            <GoogleMapsProvider>
              <RouteTracker />
              <AppContent />
            </GoogleMapsProvider>
          </NotificationProvider>
        </SocketProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
